// 팬 공간 전용 API. 스트리머용 SupabaseApi와 분리한다.
//  인증 컨텍스트(authUserId)는 SupabaseApi가 부트스트랩에서 세팅 → getAuthUserId()로 공유.
import { supabase } from "@/utils/supabase";
import { getAuthUserId } from "./SupabaseApi";

// PET — 펫 이름 공개 검색/목록 (SECURITY DEFINER RPC search_pets, 안전 컬럼만). 팬 '펫 검색'용.
//  빈 쿼리 = 전체 펫. 페이지네이션(limit/offset). 반환 { items, total }.
const Pet = {
  search: async (query = "", { limit = 10, offset = 0 } = {}) => {
    const q = (query || "").trim();
    const { data, error } = await supabase.rpc("search_pets", { p_query: q, p_limit: limit, p_offset: offset });
    if (error) {
      console.error(error);
      return { items: [], total: 0 };
    }
    const rows = data || [];
    const items = rows.map((r) => ({
      petId: r.pet_id,
      petName: r.pet_name,
      modelUrl: r.model_url,
      petImageUrl: r.pet_image_url || null,
      ownerId: r.owner_id,
      ownerName: r.owner_name,
      ownerImageUrl: r.owner_image_url || null,
      level: r.level,
    }));
    const total = rows.length ? Number(rows[0].total_count) : 0;
    return { items, total };
  },
};

// ACCOUNT — 팬 계정(fan_account). id = auth uid(user_account와 동일 규약). 팬 탭 로그인 복귀 후 ensure.
//  is_fan 플래그는 fan_account insert 트리거(sync_role_flag)가 병합. (스트리머는 user_account, 팬은 fan_account)
const Account = {
  // 팬 프로필 조회 (RLS: 본인). 없으면 null.
  fetchMine: async () => {
    const uid = getAuthUserId();
    if (!uid) return null;
    const { data } = await supabase.from("fan_account").select("*").eq("id", uid).single();
    return data || null;
  },
  // 팬 계정 프로비저닝 (idempotent). 있으면 무시.
  ensure: async () => {
    const uid = getAuthUserId();
    if (!uid) throw new Error("AUTH_USER_ID_NOT_FOUND");
    const { data: { session } = {} } = await supabase.auth.getSession();
    const u = (session && session.user) || {};
    const meta = u.user_metadata || {};
    await supabase.from("fan_account").upsert(
      {
        id: uid,
        name: meta.full_name || meta.name || null,
        image_url: meta.avatar_url || meta.picture || null,
      },
      { onConflict: "id", ignoreDuplicates: true },
    );
  },
};

// FOLLOW — 팬 → 펫 팔로우 (fan_pet_follow, RLS 본인). fan_id = auth uid.
const Follow = {
  // 내가 팔로우한 pet_id 목록 (최근순)
  fetchMine: async () => {
    const uid = getAuthUserId();
    if (!uid) return [];
    const { data, error } = await supabase
      .from("fan_pet_follow")
      .select("pet_id, created_at")
      .eq("fan_id", uid)
      .order("created_at", { ascending: false });
    if (error) {
      console.error(error);
      return [];
    }
    return (data || []).map((r) => r.pet_id);
  },
  // 내가 팔로우한 펫 상세 (공개 RPC get_followed_pets, 안전 컬럼만). 팬 홈 카드용.
  fetchMyPets: async () => {
    const { data, error } = await supabase.rpc("get_followed_pets");
    if (error) {
      console.error(error);
      return [];
    }
    return (data || []).map((r) => ({
      petId: r.pet_id,
      petName: r.pet_name,
      modelUrl: r.model_url,
      petImageUrl: r.pet_image_url || null,
      ownerId: r.owner_id,
      ownerName: r.owner_name,
      ownerImageUrl: r.owner_image_url || null,
      level: r.level,
      followedAt: r.followed_at,
    }));
  },
  // 특정 펫 팔로우 여부
  isFollowing: async (petId) => {
    const uid = getAuthUserId();
    if (!uid || !petId) return false;
    const { data } = await supabase
      .from("fan_pet_follow")
      .select("pet_id")
      .eq("fan_id", uid)
      .eq("pet_id", petId)
      .maybeSingle();
    return !!data;
  },
  // 팔로우 (idempotent)
  follow: async (petId) => {
    const uid = getAuthUserId();
    if (!uid) throw new Error("AUTH_USER_ID_NOT_FOUND");
    const { error } = await supabase
      .from("fan_pet_follow")
      .upsert({ fan_id: uid, pet_id: petId }, { onConflict: "fan_id,pet_id", ignoreDuplicates: true });
    if (error) throw error;
  },
  // 언팔로우
  unfollow: async (petId) => {
    const uid = getAuthUserId();
    if (!uid) throw new Error("AUTH_USER_ID_NOT_FOUND");
    const { error } = await supabase.from("fan_pet_follow").delete().eq("fan_id", uid).eq("pet_id", petId);
    if (error) throw error;
  },
};

// REACTION — 팬은 "작성(creator) 기준". 스트리머(SupabaseApi.Reaction)는 "소유(user_reaction) 기준"이라 다름.
const _genReactionId = () => {
  const bytes = new Uint8Array(10);
  window.crypto.getRandomValues(bytes);
  return "rct_" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};
const Reaction = {
  // 내가 만든 리액션 전부 (creator_id = 나). draft(미제출, 매핑 없음) 포함, 소유 무관.
  //  RLS read 정책이 creator_id=auth.uid() 포함이라 셀렉트만으로 됨(RPC 불필요).
  fetchMyCreated: async () => {
    const uid = getAuthUserId();
    if (!uid) return [];
    const { data, error } = await supabase
      .from("reaction")
      .select("id, label, tags, cmds, created_at")
      .eq("creator_id", uid)
      .order("created_at", { ascending: false });
    if (error) {
      console.error(error);
      return [];
    }
    return (data || []).map((r) => ({
      id: r.id,
      label: r.label || "",
      tags: r.tags || [],
      cmds: r.cmds || [],
      createdAt: r.created_at || null,
    }));
  },
  // 팬 생성 = draft: reaction만 insert (creator=나, 비시스템), user_reaction 매핑 없음(=미제출).
  //  스트리머 create(SupabaseApi.Reaction.create)와 달리 소유(user_reaction)를 넣지 않는다.
  create: async ({ label = "", tags = [], cmds = [] }) => {
    const uid = getAuthUserId();
    if (!uid) throw new Error("E_NOT_LOGGED_IN");
    const id = _genReactionId();
    const { error } = await supabase
      .from("reaction")
      .insert({ id, creator_id: uid, is_system: false, is_hide: false, label, tags, cmds });
    if (error) throw error;
    return id;
  },
};

export default { Pet, Account, Follow, Reaction };
